import * as liveVideo from '@/libraries/api/liveVideo'
import * as flags from '@/libraries/util/flags'
import moment from 'moment'

export const ViewerModule = {
  namespaced: true,
  state: {
    isPlaying: true,
    isFullscreen: false,
    isDownloadingClip: false,
    livestream: {
      url: undefined,
      streamExpiration: 0,
      cameraId: undefined,
    },
    archive: {
      playbackRate: 1.0,
      timestamp: (function () {
        return moment().subtract(1, 'minute').utc().format()
      })(),
    },
  },
  mutations: {
    setIsPlaying (state, isPlaying) {
      state.isPlaying = isPlaying
    },
    setTimestamp (state, timestamp) {
      state.archive.timestamp = timestamp
    },
    setLivestream (state, livestream) {
      state.livestream = livestream
    },
    stopLivestream (state) {
      state.livestream = {
        url: undefined,
        streamExpiration: 0,
      }
      // console.debug(`Livestream stopped: `, state.livestream)
    },
    setPlaybackRate (state, playbackRate) {
      state.archive.playbackRate = playbackRate
    },
    setFullScreen (state, isFullscreen) {
      state.isFullscreen = isFullscreen
    },
    setDownloadingClip (state, isDownloading) {
      state.isDownloadingClip = isDownloading
    },
  },
  getters: {
    /**
     * @returns The state of the video player.
     */
    isPlaying (state) {
      return state.isPlaying
    },
    /**
     * Checks of the viewer is fullscreen.
     *
     * @param {any} state The state of the module.
     * @returns `true` if the viewer is fullscreen, else `false`.
     */
    isFullscreen (state) {
      return state.isFullscreen
    },
    /**
     * @returns `true` if the video player is streaming live video,
     * else returns `false`.
     */
    isLive (state) {
      if (!state.livestream) {
        return false
      }
      const now = new Date().valueOf()
      const expiration = state.livestream.streamExpiration

      if (expiration <= 0) {
        return false
      }

      const expireTimeIsPast = expiration < now

      if (expireTimeIsPast) {
        return false
      }

      return state.livestream.url !== undefined
    },
    /**
     * Returns the playback rate.
     *
     * @param {any} state The state of the module.
     * @returns A string describing the playback rate.
     */
    playbackRate (state) {
      return state.archive.playbackRate
    },
    /**
     * @returns The timestamp that the player is up to in the archive.
     */
    timestamp (state) {
      return state.archive.timestamp
    },
    /**
     * Returns the URL for the live stream.
     *
     * @description Returns undefined if the URL is expired.
     * @param {any} state The state of the store module.
     * @returns A URL pointing to an m3u8 file, or undefined, if the URL is expired.
     */
    livestreamURL (state) {
      const now = new Date().valueOf()
      const expireTimeIsPast = state.livestream.streamExpiration < now
      if (!state.livestream) {
        return undefined
      }

      if (expireTimeIsPast) {
        return undefined
      }

      return state.livestream.url
    },
    isDownloadingClip (state) {
      return state.isDownloadingClip
    },
  },
  actions: {
    /**
     * Asks the server to spin up live video for a specific camera.
     *
     * @param {any} context The context object supplied by the store.
     * @param {any} cameraId The camera ID that the user wants to watch.
     * @returns A promise with information about the live video.
     */
    startLiveVideoForCameraUUID (context, cameraId) {
      // console.log(`Enabling Live Streaming for camera  ${cameraId.substr(0, 4)}...${cameraId.substr(cameraId.length - 5, 4)}`)
      const shouldDemo = flags.FallbackToTestStream.enabled
      return liveVideo.startStreamForCameraUUID(cameraId).then(res => {
        // console.debug(`Response: ${res}`)
        return res.data
      })
        .then(data => {
          if (data) {
            const ttlInMilliseconds = data.time_to_live * 1000
            const streamExpiration = new Date().valueOf() + ttlInMilliseconds
            let url = ''
            if (shouldDemo) {
              url = `https://video-dev.github.io/streams/x36xhzz/x36xhzz.m3u8`
            } else {
              const dir = data.dir
              url = `https://lv.api.owal.io/lv/${dir}/stream.m3u8`
            }

            const payload = {
              url, streamExpiration, cameraId,
            }

            /** Start Heartbeat */
            const heartbeatInterval = data.time_to_live - 30
            liveVideo.putHeartbeat(cameraId, heartbeatInterval)
            return context.commit('setLivestream', payload)
          }
        }).catch((error) => {
          console.error(`Error enabling live streaming: ${error}.`)

          if (shouldDemo) {
          /**
           * Demo code:
           */
            const streamExpiration = new Date().valueOf() + 1000 * 60 * 60
            const demoPayload = {
              url: `https://video-dev.github.io/streams/x36xhzz/x36xhzz.m3u8`,
              streamExpiration,
              cameraId,
            }
            return context.commit('setLivestream', demoPayload)
          }
        })
    },
    /**
     * Turns off the live stream heartbeat and updates the state.
     *
     * @param {any} context The context object supplied by the store.
     */
    stopLivestream (context) {
      liveVideo.stopHeartbeat()
      return context.commit('stopLivestream')
    },
  },
}
